import React, { useEffect, useState } from "react";
import { Form, Input, Button, Card, message, Space, Typography } from "antd";
import { MailOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import { forgotPassword, clearError } from "../../store/authSlice";
import { Link } from "react-router-dom";
import "./AuthForms.scss";
import Logo from "../../asset/images/logo.png";

const { Title, Text } = Typography;

interface ForgotPasswordFormData {
  email: string;
}

const ForgotPasswordForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isLoading, error } = useAppSelector((state) => state.auth);
  const [form] = Form.useForm();
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleSubmit = async (values: ForgotPasswordFormData) => {
    try {
      const result = await dispatch(forgotPassword(values.email)).unwrap();
      message.success(result.message || "Reset link sent to your email!");
      setEmailSent(true);
      form.resetFields();
    } catch (err) {
      // Error is handled by Redux and shown via useEffect
    }
  };

  if (emailSent) {
    return (
      <div className="auth-container">
        <Card className="auth-card">
          <div className="auth-header">
            <img
              src={Logo}
              style={{ height: "35px", marginBottom: "10px" }}
              alt="Logo"
            />
            <br />
            <Title level={3} className="auth-title">
              Check your email
            </Title>
            <Text className="auth-subtitle">
              We've sent a password reset link to your email address. Please
              check your inbox and follow the instructions to reset your
              password.
            </Text>
          </div>

          <div className="auth-footer">
            <Space direction="vertical" align="center" className="w-100">
              <Text>
                Didn't receive the email?{" "}
                <Button
                  type="link"
                  onClick={() => setEmailSent(false)}
                  className="auth-link"
                  style={{ padding: 0 }}
                >
                  Try again
                </Button>
              </Text>
              <Link to="/login" className="auth-link">
                Back to Sign In
              </Link>
            </Space>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <Card className="auth-card">
        <div className="auth-header">
          <img
            src={Logo}
            style={{ height: "35px", marginBottom: "10px" }}
            alt="Logo"
          />
          <br />
          <Title level={3} className="auth-title">
            Forgot your password?
          </Title>
          <Text className="auth-subtitle">
            Enter your email address and we'll send you a link to reset your
            password.
          </Text>
        </div>

        <Form
          form={form}
          name="forgot-password"
          onFinish={handleSubmit}
          layout="vertical"
          size="middle"
          className="auth-form"
        >
          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: "Please enter your email address" },
              { type: "email", message: "Please enter a valid email address" },
            ]}
          >
            <Input
              style={{ borderRadius: "0px" }}
              placeholder="Enter your email address"
              type="email"
            />
          </Form.Item>

          <Form.Item className="auth-submit">
            <Button
              style={{ borderRadius: "0px" }}
              type="primary"
              htmlType="submit"
              loading={isLoading}
              block
              className="auth-button"
            >
              {isLoading ? "Sending..." : "Send Reset Link"}
            </Button>
          </Form.Item>
        </Form>

        <div className="auth-footer">
          <Space direction="vertical" align="center" className="w-100">
            <Link to="/login" className="auth-link">
              Back to Sign In
            </Link>
            <Text>
              Don't have an account?{" "}
              <Link to="/signup" className="auth-link">
                Sign Up
              </Link>
            </Text>
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default ForgotPasswordForm;
